import { useRef, useState } from "react";
import axios from "axios";
import { toPng } from "html-to-image";
import { API } from "@/lib/api";
import { copyText, sessionId, trackPartner } from "@/lib/partners";
import { DigitalCard } from "@/components/partner/DigitalCard";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Check, Copy, Download, ExternalLink, Loader2 } from "lucide-react";

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.22em] text-white/45">{label}</span>
    <Input
      {...props}
      className="mt-2 h-12 rounded-none bg-transparent border-0 border-b border-white/20 text-white text-base px-0 focus-visible:ring-0 focus-visible:border-[#c19a6b]"
    />
  </label>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-start gap-3 cursor-pointer group">
    <Checkbox data-testid={testid} checked={checked} onCheckedChange={onChange} className="mt-0.5 border-white/30 data-[state=checked]:bg-[#c19a6b] data-[state=checked]:border-[#c19a6b]" />
    <span className="text-sm text-white/65 group-hover:text-white/90 transition-colors duration-200 leading-snug">{label}</span>
  </label>
);

export const ActivationPanel = ({ slug, realtor, links, programName, clientMessage, activated }) => {
  const [form, setForm] = useState({
    name: realtor.display_name || "",
    email: realtor.email || "",
    phone: realtor.phone || "",
    address: "",
    wants_digital: true,
    wants_printed: false,
    wants_call: false,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(activated);
  const cardRef = useRef();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/public/partners/${slug}/activate`, { ...form, session_id: sessionId() });
      setDone(true);
      window.scrollTo({ top: document.getElementById("activate")?.offsetTop - 40, behavior: "smooth" });
    } catch (e) {
      toast.error("We couldn't activate the program just now. Please try again or contact ARK.");
    } finally {
      setBusy(false);
    }
  };

  const downloadCard = async () => {
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 3, cacheBust: true, backgroundColor: "#0B0B0B" });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `ark-${slug}-client-card.png`;
      a.click();
      trackPartner(slug, "download_card");
    } catch {
      toast.error("Card download failed — please try again");
    }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto text-center fade-up" data-testid="activation-success">
        <div className="w-12 h-12 border border-[#c19a6b] rounded-full flex items-center justify-center mx-auto mb-8">
          <Check size={20} className="text-[#c19a6b]" />
        </div>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-white">You're all set.</h2>
        <p className="text-white/55 mt-5 leading-relaxed">
          Your {programName} is now active. Everything below is yours to share — ARK handles the rest.
        </p>

        <div className="grid sm:grid-cols-[1fr_auto] gap-8 items-center bg-white/[0.04] border border-white/10 p-7 mt-12 text-left">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#c19a6b]">Your client link</p>
            <p className="text-white text-sm mt-2 break-all" data-testid="activation-client-link">{links.client_url}</p>
          </div>
          <div className="bg-white p-2.5 justify-self-start">
            <img src={`${links.qr_image_url}?size=6`} alt="Your QR code" crossOrigin="anonymous" className="w-24 h-24 block" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-6">
          <button data-testid="activation-copy-link" onClick={() => { copyText(links.client_url, "Client link copied"); trackPartner(slug, "copy_client_link"); }} className="h-12 border border-white/20 hover:border-[#c19a6b] text-white text-[11px] uppercase tracking-[0.2em] transition-colors duration-200 inline-flex items-center justify-center gap-2">
            <Copy size={13} /> Copy Client Link
          </button>
          <a data-testid="activation-download-qr" href={`${links.qr_image_url}?size=16`} download onClick={() => trackPartner(slug, "download_qr")} className="h-12 border border-white/20 hover:border-[#c19a6b] text-white text-[11px] uppercase tracking-[0.2em] transition-colors duration-200 inline-flex items-center justify-center gap-2">
            <Download size={13} /> Download QR Code
          </a>
          <button data-testid="activation-download-card" onClick={downloadCard} className="h-12 border border-white/20 hover:border-[#c19a6b] text-white text-[11px] uppercase tracking-[0.2em] transition-colors duration-200 inline-flex items-center justify-center gap-2">
            <Download size={13} /> Download Digital Card
          </button>
          <a data-testid="activation-view-client" href={links.client_url} target="_blank" rel="noreferrer" className="h-12 bg-[#c19a6b] hover:bg-[#b08a5c] text-white text-[11px] uppercase tracking-[0.2em] transition-colors duration-200 inline-flex items-center justify-center gap-2">
            <ExternalLink size={13} /> View Client Experience
          </a>
        </div>

        <div className="mt-14 text-left bg-white/[0.04] border border-white/10 p-7">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#c19a6b]">Send this to a client</p>
          <p className="text-white/70 text-sm leading-relaxed whitespace-pre-line mt-4" data-testid="activation-client-message">{clientMessage}</p>
          <button
            data-testid="activation-copy-message"
            onClick={() => copyText(clientMessage, "Message copied")}
            className="mt-6 h-11 px-6 border border-white/20 hover:border-[#c19a6b] text-white text-[11px] uppercase tracking-[0.2em] transition-colors duration-200 inline-flex items-center gap-2"
          >
            <Copy size={13} /> Copy Message
          </button>
        </div>

        <div className="absolute -left-[9999px] top-0" aria-hidden="true">
          <DigitalCard ref={cardRef} realtor={realtor} links={links} programName={programName} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="activation-form">
      <div className="space-y-8">
        <Field label="Your name" data-testid="activation-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
        <div className="grid sm:grid-cols-2 gap-8">
          <Field label="Email" data-testid="activation-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
          <Field label="Phone" data-testid="activation-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        </div>
        <Field label="Delivery address for printed materials (optional)" data-testid="activation-address" value={form.address} onChange={(e) => set("address", e.target.value)} />
      </div>

      <div className="space-y-4 mt-10">
        <Toggle testid="activation-wants-digital" label="Send me my digital client materials" checked={form.wants_digital} onChange={(v) => set("wants_digital", !!v)} />
        <Toggle testid="activation-wants-printed" label="I'd like complimentary printed client cards" checked={form.wants_printed} onChange={(v) => set("wants_printed", !!v)} />
        <Toggle testid="activation-wants-call" label="I'd like to speak with ARK" checked={form.wants_call} onChange={(v) => set("wants_call", !!v)} />
      </div>

      <button
        data-testid="activate-submit-btn"
        onClick={submit}
        disabled={busy}
        className="w-full mt-12 h-14 bg-[#c19a6b] hover:bg-[#b08a5c] text-white text-xs uppercase tracking-[0.25em] font-bold btn-lift disabled:opacity-60 inline-flex items-center justify-center gap-2"
      >
        {busy && <Loader2 size={15} className="animate-spin" />} Activate My Program
      </button>
      <p className="text-center text-[11px] text-white/35 mt-5 tracking-wide">
        No contract · No minimum referrals · No cost
      </p>
    </div>
  );
};
